// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDModal from "components/MDModal";

// Material Dashboard 2 PRO React examples
import DataTable from "examples/Tables/DataTable";
// Data
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import { Button, Grid, IconButton, Tooltip } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ModalContext } from "context/ModalCtx";

import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import MDLink from "components/MDLink";
import { Add, Edit, Delete } from "@mui/icons-material";
import { TABLE_FILTERS } from "utils/constants";
import {
  getFiltersFromCache,
  saveFiltersToCache,
  getSortByFromCache,
  saveSortByToCache,
} from "utils/cacheManager";
import DefaultCell from "examples/Tables/components/DefaultCell";
import { STATUS_BUTTON_VALUE } from "utils/constants";
import moment from "moment";
import { LoginFormContext } from "context/loginFormCtx";
import {
  getSuperAdminUsers,
  updateSuperAdminUser,
  deleteSuperAdminUser,
} from "lib/Api/SuperAdminUsers";
import { updateSuperAdminUserStatus } from "lib/Api/SuperAdminUsers";

function SuperAdminUsers() {
  const { formIsEdit } = useContext(ModalContext);

  const { userDetail, setNotification, showMessageNotification } = useContext(LoginFormContext);

  const navigate = useNavigate();

  const handleStatusChange = async (id, status) => {
    showMessageNotification({
      title: "Are you sure?",
      text: "You want to change the status of this admin user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--cc-confirm-button-color)",
      cancelButtonColor: "var(--cc-cancel-button-color)",
      confirmButtonText: "Yes, change it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await updateSuperAdminUserStatus({ id, status: status ? 0 : 1 });

        if (response?.status === 200) {
          getSuperAdminUsersData();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
        } else {
          setNotification({
            color: "error",
            icon: "warning",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      }
    });
  };

  const handleDeleteUser = async (id) => {
    showMessageNotification({
      title: "Are you sure?",
      text: "You want to delete this admin user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--cc-confirm-button-color)",
      cancelButtonColor: "var(--cc-cancel-button-color)",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteSuperAdminUser(id);

        if (response?.status === 200) {
          getSuperAdminUsersData();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
        } else {
          setNotification({
            color: "error",
            icon: "check",
            title: "error",
            content: response.data.message,
            open: true,
          });
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "first_name",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value || ""} />,
      },
      {
        Header: "Last Name",
        accessor: "last_name",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value || ""} />,
      },
      {
        Header: "Email",
        accessor: "email",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Created At",
        accessor: "created_at",
        filterInputType: "date",
        sort: true,
        disableFuture: true,
        Cell: ({ value }) => <DefaultCell value={moment(value).format("MM/DD/YYYY")} />,
      },
      {
        Header: "Updated At",
        accessor: "updated_at",
        filterInputType: "date",
        sort: true,
        disableFuture: true,
        Cell: ({ value }) => <DefaultCell value={moment(value).format("MM/DD/YYYY")} />,
      },
      {
        Header: "Action",
        width: "8%",
        sort: false,
        Cell: ({ value, row }) => {
          return (
            <MDBox sx={{ display: "flex" }}>
              <Tooltip title="Edit User" placement="top">
                <IconButton
                  onClick={() => {
                    navigate(`/super-admin/${row?.original?.id}/edit`, {
                      state: { isEditMode: true },
                    });
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  String(userDetail?.id) === String(row?.original?.id)
                    ? "You can't delete yourself"
                    : "Delete User"
                }
                placement="top"
              >
                <span>
                  <IconButton
                    disabled={String(userDetail?.id) === String(row?.original?.id)}
                    onClick={() => {
                      handleDeleteUser(row?.original?.id);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          );
        },
      },
    ],
    [userDetail?.id]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const [rows, setRows] = useState([]);

  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(getFiltersFromCache(TABLE_FILTERS.ADMIN_USERS) || []);
  const [sortBy, setSortBy] = useState(getSortByFromCache(TABLE_FILTERS.ADMIN_USERS) || []);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
    setPage(0);
  };

  const handleFiltersChange = (filterObject) => {
    setFilters(filterObject);
    saveFiltersToCache(TABLE_FILTERS.ADMIN_USERS, filterObject);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    saveFiltersToCache(TABLE_FILTERS.ADMIN_USERS, []);
    setPage(0);
  };

  const handleSortByChange = (sortObject) => {
    setSortBy(sortObject);
    saveSortByToCache(TABLE_FILTERS.ADMIN_USERS, sortObject);
  };

  const getSuperAdminUsersData = async () => {
    try {
      setIsLoading(true);
      const sortId = sortBy[0]?.id;
      const sortVal = sortBy[0]?.desc ? "desc" : "asc";

      const updatedFilters = filters.map((filter) => {
        if (filter.id == "status") {
          return {
            ...filter,
            value: filter.value.value,
          };
        }
        return filter;
      });

      const filterBody = {
        page: page + 1,
        pageSize,
        filters: updatedFilters,
        sortBy: sortId ? [{ id: sortId, value: sortVal }] : [],
      };

      const response = await getSuperAdminUsers(filterBody);

      if (response && response.status === 200) {
        const responseData = response.data.data;

        setRows(responseData.rows);

        setRowCount(responseData.count);
        setTotalPages(Math.ceil(responseData.count / pageSize));
      }
    } catch (error) {
      console.log("error (getSuperAdminUsersData): ", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSuperAdminUsersData();
  }, [page, pageSize, filters, sortBy]);

  return (
    <CustomPageLayout>
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <Grid container justifyContent="space-between" spacing={3}>
              <Grid item>
                <MDTypography variant="h5" fontWeight="medium">
                  Manage Super Admin Users
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Here you can manage all super admin users.
                </MDTypography>
              </Grid>
              <Grid item>
                <MDLink to="/super-admin/add-admin">
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => {}}>
                    Add Super Admin
                  </Button>
                </MDLink>
              </Grid>
            </Grid>
          </MDBox>

          <DataTable
            isLoading={isLoading}
            table={{ columns, rows }}
            pageSize={pageSize}
            currentPage={page}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            filters={filters}
            handleFiltersChange={handleFiltersChange}
            sorting={sortBy}
            handleSortByChange={handleSortByChange}
            getData={getSuperAdminUsersData}
            handleClearAllFilters={handleClearAllFilters}
            loading={isLoading}
            rowCount={rowCount}
          />
        </Card>
      </MDBox>
    </CustomPageLayout>
  );
}

export default SuperAdminUsers;
